/**
 * Parallax Project Hero
 * 
 * Creates a stunning depth effect with layered text that moves at different speeds
 * as the user scrolls, creating a 3D parallax effect with text appearing both
 * behind and in front of the hero image.
 */

'use client';

import {
    motion,
    useScroll,
    useSpring,
    useTransform,
} from 'framer-motion';
import Image from 'next/image';
import { useRef } from 'react';

export interface ParallaxProjectHeroProps {
  /** Project name - will be split for parallax effect */
  projectName: string;
  /** Hero image URL */
  heroImageUrl: string;
  /** Project tagline or subtitle */
  tagline?: string;
  /** Height of hero section (default: 100vh) */
  height?: string;
  /** Custom brand colors */
  brandColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
}

/**
 * ParallaxProjectHero Component
 * 
 * Creates a depth effect by:
 * 1. Placing first word(s) behind the image (slower movement)
 * 2. Image in the middle layer
 * 3. Last word(s) in front of the image (faster movement)
 */
export function ParallaxProjectHero({
  projectName,
  heroImageUrl,
  tagline,
  height = '100vh',
  brandColors = {
    primary: '#243462',
    secondary: '#040813',
    accent: '#BBA339',
  },
}: ParallaxProjectHeroProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  // Smooth spring configuration for natural motion
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };

  // Parallax transforms for different layers
  // Background text (behind image) - moves slowest
  const yBackground = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 150]),
    springConfig
  );
  
  // Middle layer (image) - moves at medium speed
  const yMiddle = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 250]),
    springConfig
  );
  
  // Foreground text (in front of image) - moves fastest
  const yForeground = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 400]),
    springConfig
  );

  // Opacity fade for all elements
  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0]),
    springConfig
  );

  // Scale for subtle zoom effect on image
  const scale = useSpring(
    useTransform(scrollYProgress, [0, 1], [1, 1.1]),
    springConfig
  );

  // Split project name into words
  const words = projectName.split(' ');
  const midPoint = Math.ceil(words.length / 2);
  const backgroundWords = words.slice(0, midPoint).join(' ');
  const foregroundWords = words.slice(midPoint).join(' ');

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden bg-linear-to-b from-gray-50 to-white"
      style={{ height, minHeight: height }}
    >
      {/* Background Layer - "Mangrove" (behind image) - Positioned at top, slower, gold */}
      <motion.div
        style={{
          y: yBackground,
          opacity,
        }}
        className="absolute inset-0 flex items-start justify-center pt-32 md:pt-40 lg:pt-48 pointer-events-none z-10 px-4"
      >
        <h1
          className="text-[15vw] md:text-[18vw] lg:text-[20vw] xl:text-[22vw] font-black tracking-tighter leading-none text-center whitespace-nowrap font-inter"
          style={{
            color: brandColors.accent,
            opacity: 0.4,
            maxWidth: '100%',
          }}
        >
          {backgroundWords}
        </h1>
      </motion.div>

      {/* Middle Layer - Hero Image - Zoomed 120%, anchored to bottom */}
      <motion.div
        style={{
          y: yMiddle,
          scale,
          opacity,
        }}
        className="absolute inset-0 z-20 flex items-end justify-center"
      >
        <div className="relative h-full w-full">
          <div className="relative w-full h-full" style={{ transform: 'scale(1.2)', transformOrigin: 'bottom center' }}>
            <Image
              src={heroImageUrl}
              alt={projectName}
              fill
              className="object-contain object-bottom"
              priority
              sizes="120vw"
            />
          </div>
        </div>
      </motion.div>

      {/* Foreground Layer - "Bay" (in front of image) - Positioned at bottom, right-aligned, faster, gold */}
      <motion.div
        style={{
          y: yForeground,
          opacity,
        }}
        className="absolute inset-0 flex items-end justify-end pb-32 md:pb-40 lg:pb-48 pr-8 md:pr-12 lg:pr-16 pointer-events-none z-30"
      >
        <h1
          className="text-[18vw] md:text-[22vw] lg:text-[26vw] font-black tracking-tighter leading-none text-right font-inter"
          style={{
            color: brandColors.accent,
            opacity: 0.5,
          }}
        >
          {foregroundWords}
        </h1>
      </motion.div>

      {/* Tagline/Subtitle - Fades in as you scroll */}
      {tagline && (
        <motion.div
          style={{
            opacity: useSpring(
              useTransform(scrollYProgress, [0, 0.3], [0, 1]),
              springConfig
            ),
          }}
          className="absolute bottom-24 left-0 right-0 z-40 flex justify-center px-6"
        >
          <div
            className="bg-white/95 backdrop-blur-sm px-8 py-4 rounded-full shadow-xl border"
            style={{ borderColor: `${brandColors.accent}40` }}
          >
            <p
              className="text-sm md:text-base lg:text-lg font-semibold tracking-wide uppercase text-center font-inter"
              style={{ 
                color: brandColors.primary 
              }}
            >
              {tagline}
            </p>
          </div>
        </motion.div>
      )}

      {/* Scroll Indicator */}
      <motion.div
        style={{
          opacity: useSpring(
            useTransform(scrollYProgress, [0, 0.2], [1, 0]),
            springConfig
          ),
        }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2"
      >
        <span
          className="text-xs uppercase tracking-widest font-medium font-inter"
          style={{ 
            color: brandColors.primary 
          }}
        >
          Scroll
        </span>
        <motion.div
          animate={{
            y: [0, 8, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="w-6 h-10 rounded-full border-2 flex items-start justify-center p-1.5"
          style={{ borderColor: brandColors.primary }}
        >
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: brandColors.accent }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
}

